import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Combobox, Heading, Switch } from '@restworld/ui-ds-base';
import {
  employerJobListingJpStatusFilterAtom,
  employerJobListingLocationFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
  employerJobListingOccupationFilterAtom,
} from '../employer-job-listing.atoms';
import { useAtomValue } from 'jotai';
import useEmployerRestaurantLocations from '../../restaurants/hooks/use-employer-restaurant-locations';
import useEmployerRestaurantNames from '../../restaurants/hooks/use-employer-restaurant-names';
import { cn } from '@restworld/utils-common';
import useEmployerJobListingOccupations from '../hooks/use-employer-job-listing-occupations';

export function JobListingJpStatusFilter() {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultJpStatus = useAtomValue(employerJobListingJpStatusFilterAtom);

  return (
    <FiltersContainer title="">
      <Controller
        name="jpStatus"
        defaultValue={defaultJpStatus}
        control={control}
        render={({ field }) => {
          const isChecked = field.value?.includes('online') || false;
          const onChange = (checked: boolean) => {
            field.onChange(checked ? ['online'] : []);
          };
          return (
            <SwitchContainer>
              <SwitchLabel>{translateCommon('show_only_open_positions')}</SwitchLabel>
              <Switch
                checked={isChecked}
                onChange={onChange}
                srOnlyLabel={translateCommon('show_only_open_positions')}
                size="md"
              />
            </SwitchContainer>
          );
        }}
      />
    </FiltersContainer>
  );
}

export function JobListingLocationFilter({ ...rest }) {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultLocation = useAtomValue(employerJobListingLocationFilterAtom);
  const locations = useEmployerRestaurantLocations();

  return (
    <FiltersContainer title={translateCommon('location')}>
      <Controller
        name="location"
        control={control}
        defaultValue={defaultLocation}
        render={({ field }) => (
          <Combobox
            {...rest}
            value={field.value}
            onChange={(location: string) => {
              field.onChange(location);
            }}
            dropdownList={locations.data}
            placeholder={translateCommon('select_location')}
            fullwidth
          />
        )}
      />
    </FiltersContainer>
  );
}

export function JobListingRestaurantNameFilter({ ...rest }) {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultRestaurantName = useAtomValue(employerJobListingRestaurantNameFilterAtom);
  const restaurantInfo = useEmployerRestaurantNames();

  const dropdownList = useMemo(
    () => restaurantInfo?.data?.map((restaurant) => restaurant.restaurant_name),
    [restaurantInfo?.data]
  );

  return (
    <FiltersContainer title={translateCommon('restaurant_name')}>
      <Controller
        name="restaurantName"
        control={control}
        defaultValue={defaultRestaurantName}
        render={({ field }) => (
          <Combobox
            {...rest}
            value={field.value}
            onChange={(location: string) => {
              field.onChange(location);
            }}
            dropdownList={dropdownList}
            placeholder={translateCommon('select_restaurant_name')}
            fullwidth
          />
        )}
      />
    </FiltersContainer>
  );
}

export function JobListingOccupationFilter({ ...rest }) {
  const { t: translateCommon } = useTranslation('common');
  const { control } = useFormContext();
  const defaultOccupationId = useAtomValue(employerJobListingOccupationFilterAtom);
  const { data: occupations, isLoading } = useEmployerJobListingOccupations();

  const dropdownList = useMemo(() => occupations?.map((occupation) => occupation.label), [occupations]);

  return (
    <FiltersContainer title={translateCommon('occupation')}>
      <Controller
        name="occupationId"
        control={control}
        defaultValue={defaultOccupationId}
        render={({ field }) => {
          // The Combobox works with display labels, but the backend filters by
          // `occupation_id`. We keep the id in the form value and map id <-> label here.
          const selectedLabel = occupations?.find((occupation) => occupation.id === field.value)?.label ?? '';
          return (
            <Combobox
              {...rest}
              value={selectedLabel}
              isLoading={isLoading}
              onChange={(label: string) => {
                const matched = occupations?.find((occupation) => occupation.label === label);
                field.onChange(matched?.id ?? '');
              }}
              dropdownList={dropdownList}
              placeholder={translateCommon('select_occupation')}
              fullwidth
            />
          );
        }}
      />
    </FiltersContainer>
  );
}

const FiltersContainer = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <FilterContainer>
    <FilterTitle title={title} />
    <MainContent>{children}</MainContent>
  </FilterContainer>
);

const FilterContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);

const SwitchContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-between gap-3', className)} {...props} />
);

const SwitchLabel = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-neutral-300 font-medium text-base', className)} {...props} />
);

const MainContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('', className)} {...props} />
);

const FilterTitle = ({ title }: { title: string }) => (
  <Heading size={5} weight="medium" className="leading-6">
    {title}
  </Heading>
);
